<template>
  <BModal v-model="isVisible"
          size="lg"
          centered
          scrollable
          hide-footer
          hide-header
          body-class="p-0"
          content-class="rounded-xl overflow-hidden shadow-2xl border-0">
    <template #title>
      <h5 class="h5 mb-0">Brand Auswählen</h5>
      <p class="text-muted mb-0 small">
        Hierarchische Auswahl von Fahrzeugkategorien, Marken und Unterkategorien
      </p>
    </template>

    <!-- Footer -->
    <template #footer>
      <div class="text-muted small">{{ selectedCount }} Brands ausgewählt</div>
      <div class="d-flex gap-2">
        <BButton variant="outline-secondary" @click="close">Abbrechen</BButton>
        <BButton variant="primary" @click="apply">Übernehmen</BButton>
      </div>
    </template>

      <!-- Body -->
    <div class="flex flex-col h-full">
      <div class="p-4 pt-2 flex-grow-1 overflow-hidden d-flex flex-column">
        <!-- Search -->
        <div class="position-relative mb-3">
          <Search class="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" :size="16" />
          <input
            type="text"
            class="form-control ps-5"
            placeholder="Brand, Kategorie oder Unterkategorie suchen..."
            v-model="searchQuery"
          >
        </div>

        <!-- Tree View -->
        <div class="border rounded overflow-hidden flex-grow-1 overflow-auto" style="max-height: 450px;">
          <div v-for="group in filteredTreeData" :key="group.id" class="border-bottom last-border-bottom-0">
            <!-- Group Header -->
            <div class="p-2 d-flex align-items-center group-header">
              <button
                class="btn btn-sm btn-icon p-1 me-2 text-muted hover-bg-gray-200 rounded"
                @click="toggleExpand(group)"
              >
                <ChevronDown v-if="group.expanded" :size="16" />
                <ChevronRight v-else :size="16" />
              </button>

              <div class="d-flex align-items-center flex-grow-1 cursor-pointer" @click.stop="toggleGroup(group)">
                <div class="custom-checkbox me-2" :class="{ 'checked': group.checked, 'indeterminate': group.indeterminate }">
                  <Check v-if="group.checked" :size="12" class="text-white" />
                  <Minus v-else-if="group.indeterminate" :size="12" class="text-white" />
                </div>
                <span class="fw-medium user-select-none">{{ group.label }} ({{ group.count }})</span>
              </div>
            </div>

            <!-- Group Children -->
            <div v-if="group.expanded" class="ps-4 border-top">
              <div
                v-for="item in group.children"
                :key="item.id"
                class="p-2 d-flex align-items-center border-bottom last-border-bottom-0 hover-bg-light cursor-pointer"
                @click="toggleItem(group, item)"
              >
                <button class="btn btn-sm btn-icon p-1 me-2 invisible">
                  <ChevronRight :size="16" />
                </button>

                <div class="d-flex align-items-center flex-grow-1">
                  <div class="custom-checkbox me-2" :class="{ 'checked': item.checked }">
                    <Check v-if="item.checked" :size="12" class="text-white" />
                  </div>
                  <span class="user-select-none">{{ item.label }} ({{ item.count }})</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </BModal>
</template>

<script>
import { Search, ChevronDown, ChevronRight, Check, Minus } from 'lucide-vue-next';

export default {
  name: 'BrandFilterModal',

  components: {
    Search,
    ChevronDown,
    ChevronRight,
    Check,
    Minus
  },

  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    initialData: {
      type: Array,
      default: () => []
    }
  },

  emits: ['update:modelValue', 'apply'],

  data() {
    return {
      searchQuery: '',
      // Mock data matching the HTML structure provided by user
      treeData: [
        {
          id: 'bmw-group',
          label: 'BMW Group',
          count: 6,
          expanded: true,
          checked: false,
          indeterminate: true,
          children: [
            { id: 'bmw', label: 'BMW', count: 1, checked: true },
            { id: 'mini', label: 'MINI', count: 1, checked: false },
            { id: 'rolls-royce', label: 'Rolls-Royce', count: 1, checked: false },
            { id: 'bmw-i', label: 'BMW i', count: 1, checked: true },
            { id: 'bmw-m', label: 'BMW M', count: 1, checked: false },
            { id: 'bmw-motorrad', label: 'BMW Motorrad', count: 1, checked: false }
          ]
        },
        {
          id: 'competitors',
          label: 'Competitors',
          count: 4,
          expanded: false,
          checked: false,
          indeterminate: false,
          children: [
            { id: 'audi', label: 'Audi', count: 1, checked: false },
            { id: 'mercedes', label: 'Mercedes-Benz', count: 1, checked: false },
            { id: 'tesla', label: 'Tesla', count: 1, checked: false },
            { id: 'porsche', label: 'Porsche', count: 1, checked: false }
          ]
        }
      ]
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    selectedCount() {
      let count = 0;
      this.treeData.forEach(group => {
        if (group.children) {
          group.children.forEach(child => {
            if (child.checked) count++;
          });
        }
      });
      return count;
    },
    filteredTreeData() {
      if (!this.searchQuery) return this.treeData;

      const query = this.searchQuery.toLowerCase();

      return this.treeData.map(group => {
        const groupMatches = group.label.toLowerCase().includes(query);
        const matchingChildren = group.children.filter(child =>
          child.label.toLowerCase().includes(query)
        );

        if (groupMatches || matchingChildren.length > 0) {
          return {
            ...group,
            children: groupMatches ? group.children : matchingChildren,
            expanded: true
          };
        }
        return null;
      }).filter(group => group !== null);
    }
  },

  methods: {
    close() {
      this.isVisible = false;
    },
    apply() {
      const selected = [];
      this.treeData.forEach(group => {
        if (group.children) {
          group.children.forEach(child => {
            if (child.checked) selected.push(child);
          });
        }
      });
      this.$emit('apply', selected);
      this.close();
    },
    toggleExpand(group) {
      group.expanded = !group.expanded;
    },
    toggleGroup(group) {
      const targetState = !group.checked;

      group.checked = targetState;
      group.indeterminate = false;

      if (group.children) {
        group.children.forEach(child => {
          child.checked = targetState;
        });
      }
    },
    toggleItem(group, item) {
      item.checked = !item.checked;
      this.updateGroupState(group);
    },
    updateGroupState(group) {
      if (!group.children) return;

      const allChecked = group.children.every(c => c.checked);
      const someChecked = group.children.some(c => c.checked);

      group.checked = allChecked;
      group.indeterminate = someChecked && !allChecked;
    }
  },

  watch: {
    initialData: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
           // In a real app we would merge/map this. For now we keep the mock data or overwrite if provided
           // this.treeData = JSON.parse(JSON.stringify(newVal));
        }
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss" scoped>
.custom-checkbox {
  width: 18px;
  height: 18px;
  border: 2px solid var(--bs-gray-400);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  // background-color: white;

  &.checked, &.indeterminate {
    background-color: var(--bs-primary); // Primary color
    border-color: var(--bs-primary);
  }
}

.hover-bg-light:hover {
  // background-color: var(--bs-gray-100);
}

.hover-bg-gray-200:hover {
  // background-color: var(--bs-gray-200);
}

.last-border-bottom-0:last-child {
  border-bottom: 0 !important;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}
</style>
